import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import { getActivityTimeline } from "@/modules/lead-page/api/lead-page.v2.api";
import { ActivityItem, ActivityType } from "@/modules/lead-page/dtos/index-v2";

export const useZoomCallsActivity = (
  journeyUid: string,
  options?: { enabled?: boolean; agentUids?: string[] },
) => {
  const agentUids = options?.agentUids?.length ? options.agentUids : undefined;

  const query = useInfiniteQuery({
    queryKey: ["activity-timeline-zoom", journeyUid, agentUids ?? null],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      getActivityTimeline(journeyUid, {
        limit: 3,
        type: [ActivityType.ZoomCall],
        includeChildLead: false,
        cursor: pageParam,
        direction: "desc",
        agentUids,
      }),
    getNextPageParam: (lastPage) =>
      lastPage?.data?.hasNext ? lastPage.data.nextCursor : undefined,
    enabled: (options?.enabled ?? true) && !!journeyUid,
    // A 401 means the viewer has no access to this integration — the section
    // hides on it, so retrying would only delay that.
    retry: (failureCount, error) =>
      !(axios.isAxiosError(error) && error.response?.status === 401) &&
      failureCount < 3,
  });

  const zoomItems: ActivityItem[] = useMemo(
    () =>
      (query.data?.pages ?? [])
        .flatMap((page) => page?.data?.items ?? [])
        .filter((item) => item.type === ActivityType.ZoomCall),
    [query.data],
  );

  return { ...query, zoomItems };
};
